/** Small pieces every Insights → Feedback surface shows the same way, in the
 *  Connectors idiom (cx-pill). */
import dayjs from 'dayjs';
import { DislikeFilled, LikeFilled } from '@ant-design/icons';
import { useLanguage } from '@/hooks/useLanguage';
import { datePattern } from '@/utils/locale';
import { getInitials } from '@/utils/getInitials';
import type { FeedbackRating } from '../../types/feedback';

/** A date and time in the viewer's own zone, in the app's language. */
export function formatWhen(value: string | null | undefined): string {
  if (!value) return '—';
  return dayjs(value).format(`${datePattern('monthDayYear')} · HH:mm`);
}

/** Liked / Disliked as a pill; `compact` is the icon alone (with its name for
 *  assistive technology). */
export function RatingBadge({ rating, compact = false }: { rating: FeedbackRating; compact?: boolean }) {
  const { t } = useLanguage();
  const label = t(rating === 'like' ? 'insights.feedback.rating.like' : 'insights.feedback.rating.dislike');
  return (
    <span
      className={`fb-rating fb-rating--${rating}${compact ? ' fb-rating--compact' : ''}`}
      role="img"
      aria-label={label}
      title={compact ? label : undefined}
    >
      {rating === 'like' ? <LikeFilled aria-hidden /> : <DislikeFilled aria-hidden />}
      {!compact && <span aria-hidden>{label}</span>}
    </span>
  );
}

/** The quick reasons, in the chat's own words (chat.rating.reasons.<key>). */
export function ReasonPills({ reasons, rating }: { reasons: string[]; rating: FeedbackRating }) {
  const { t } = useLanguage();
  if (!reasons.length) return <span className="fbr-row__none">—</span>;
  return (
    <span className="fb-reasons">
      {reasons.map((key) => (
        <span key={key} className={`cx-pill fb-reason fb-reason--${rating}`}>
          {t(`chat.rating.reasons.${key}`, { defaultValue: key.replace(/_/g, ' ') })}
        </span>
      ))}
    </span>
  );
}

/** A person's initials in a round chip. */
export function PersonAvatar({ name }: { name: string | null | undefined }) {
  return (
    <span className="fb-avatar" aria-hidden="true">
      {name ? getInitials(name) : '?'}
    </span>
  );
}
